import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Chip,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
  Avatar,
} from '@mui/material';
import {
  AttachMoney,
  TrendingUp,
  Receipt,
  AccountBalance,
  Search,
  Add,
  GetApp,
  Refresh,
  Download,
} from '@mui/icons-material';
import DataTable, { Column } from '../components/common/DataTable';
import KPIWidget from '../components/dashboard/KPIWidget';
import RevenueChart from '../components/dashboard/RevenueChart';
import TransactionList from '../components/transactions/TransactionList';
import ExpenseList from '../components/expenses/ExpenseList';
import ReportGenerator from '../components/reports/ReportGenerator';
import { useAppDispatch } from '../hooks/redux';
import { addNotification } from '../store/slices/uiSlice';
import { format, subMonths } from 'date-fns';
import { transactionService, TransactionSummary } from '../services/transactionService';
import '../styles/minimal-padding.css';

// Enhanced Metric Card Component with forwardRef
interface MetricCardProps {
  title: string;
  value: string;
  change: number;
  changeType: 'increase' | 'decrease';
  bgGradient: string;
  icon: React.ReactNode;
}

const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(({
  title,
  value,
  change,
  changeType,
  bgGradient,
  icon,
}, ref) => (
  <Card
    ref={ref}
    className="metric-card"
    sx={{
      background: bgGradient,
      position: 'relative',
      overflow: 'hidden',
      minHeight: 140,
      borderRadius: 4,
      border: 'none',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
      color: '#FFFFFF',
      cursor: 'pointer',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
        transform: 'translateX(-100%)',
        transition: 'transform 0.6s',
      },
      '&:hover::before': {
        transform: 'translateX(100%)',
      },
    }}
  >
    <CardContent sx={{ position: 'relative', zIndex: 2, p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#FFFFFF !important',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '1.5px',
              mb: 1,
              display: 'block',
              opacity: 0.9,
            }}
            style={{ color: '#FFFFFF !important' }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h3" 
            sx={{ 
              color: '#FFFFFF !important',
              fontWeight: 800,
              fontSize: { xs: '1.8rem', sm: '2.2rem' },
              lineHeight: 1.2,
              mb: 1,
            }}
            style={{ color: '#FFFFFF !important' }}
          >
            {value}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendingUp sx={{ fontSize: 18, color: '#FFFFFF' }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#FFFFFF !important',
                fontSize: '0.85rem',
                fontWeight: 600,
                opacity: 0.9,
              }}
              style={{ color: '#FFFFFF !important' }}
            >
              {changeType === 'increase' ? '+' : '-'}{change}%
            </Typography>
          </Box>
        </Box>
        <Avatar 
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.2)', 
            width: 70, 
            height: 70,
            color: 'white',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Box sx={{ fontSize: { xs: 30, sm: 35 } }}>
            {icon}
          </Box>
        </Avatar>
      </Box>
    </CardContent>
  </Card>
));

MetricCard.displayName = 'MetricCard';

const Finance: React.FC = () => {
  const dispatch = useAppDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [transactionSummary, setTransactionSummary] = useState<TransactionSummary | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchFinancialData = async () => {
    setLoading(true);
    try {
      const endDate = new Date();
      const startDate = subMonths(endDate, 6);
      const summary = await transactionService.getTransactionSummary(startDate.toISOString().split('T')[0], endDate.toISOString().split('T')[0]);
      setTransactionSummary(summary);
    } catch (err) {
      console.error('Failed to fetch financial data:', err);
      dispatch(addNotification({ type: 'error', message: err instanceof Error ? err.message : 'Failed to load financial data' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinancialData();
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const totalRevenue = transactionSummary?.totalIncome ?? 0;
  const totalExpenses = transactionSummary?.totalExpenses ?? 0;
  const netProfit = totalRevenue - totalExpenses;
  const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

  const stats = {
    totalRevenue,
    totalExpenses,
    netProfit,
    profitMargin,
  };

  const monthlyTrends = transactionSummary?.monthlyTrends ?? [];
  const revenueChartData = {
    labels: monthlyTrends.map((t) => t.month) || ['No data'],
    datasets: [
      {
        label: 'Revenue',
        data: monthlyTrends.map((t) => t.income) || [0],
        borderColor: 'rgb(76, 175, 80)',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        tension: 0.4,
      },
      {
        label: 'Expenses',
        data: monthlyTrends.map((t) => t.expenses) || [0],
        borderColor: 'rgb(244, 67, 54)',
        backgroundColor: 'rgba(244, 67, 54, 0.1)',
        tension: 0.4,
      },
    ],
  };

  return (
    <Box sx={{ width: '100%', height: '100%', p: 2 }}>
      {/* Enhanced Header Section with Animation */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 3, 
          background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(232, 180, 184, 0.3)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  color: '#5C2A30',
                  mb: 1,
                }}
              >
                Financial Management
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(92, 42, 48, 0.85)',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                }}
              >
                Track revenue, expenses, and financial performance
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title="Refresh Data" arrow>
                <IconButton 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<GetApp />}
                sx={{
                  color: '#5C2A30',
                  borderColor: 'rgba(92, 42, 48, 0.4)',
                  bgcolor: 'rgba(255, 255, 255, 0.4)',
                  backdropFilter: 'blur(10px)',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderRadius: 3,
                  '&:hover': {
                    borderColor: '#5C2A30',
                    bgcolor: 'rgba(255, 255, 255, 0.6)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Export Report
              </Button>
            </Box>
          </Box>
        </Box>
        
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
      </Paper>

      {/* Enhanced Financial KPIs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={300} style={{ transitionDelay: '100ms' }}>
            <MetricCard
              title="Total Revenue"
              value={`₹${(stats.totalRevenue / 1000).toFixed(0)}K`}
              change={15.2}
              changeType="increase"
              bgGradient="linear-gradient(135deg, #10B981 0%, #059669 100%)"
              icon={<AttachMoney />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={400} style={{ transitionDelay: '200ms' }}>
            <MetricCard
              title="Total Expenses"
              value={`₹${(stats.totalExpenses / 1000).toFixed(0)}K`}
              change={8.5}
              changeType="increase"
              bgGradient="linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
              icon={<Receipt />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={500} style={{ transitionDelay: '300ms' }}>
            <MetricCard
              title="Net Profit"
              value={`₹${(stats.netProfit / 1000).toFixed(0)}K`}
              change={22.8}
              changeType="increase"
              bgGradient="linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)"
              icon={<TrendingUp />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={600} style={{ transitionDelay: '400ms' }}>
            <MetricCard
              title="Profit Margin"
              value={`${stats.profitMargin.toFixed(1)}%`}
              change={5.2}
              changeType="increase"
              bgGradient="linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)"
              icon={<AccountBalance />}
            />
          </Zoom>
        </Grid>
      </Grid>

      {/* Enhanced Revenue vs Expenses Chart */}
      <Fade in timeout={700} style={{ transitionDelay: '500ms' }}>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Card 
              sx={{ 
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(232, 180, 184, 0.2)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #10B981 0%, #EF4444 100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <RevenueChart
                  title="Revenue vs Expenses Trend"
                  data={revenueChartData}
                  type="line"
                  height={300}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Fade>

      {/* Enhanced Financial Tabs */}
      <Fade in timeout={800} style={{ transitionDelay: '600ms' }}>
        <Card 
          sx={{ 
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(232, 180, 184, 0.2)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
            },
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  minHeight: 64,
                  '&.Mui-selected': {
                    color: '#E8B4B8',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#E8B4B8',
                  height: 3,
                },
              }}
            >
              <Tab label="Transactions" />
              <Tab label="Expenses" />
              <Tab label="Reports" />
            </Tabs>
          </Box>

        <Box sx={{ p: 3 }}>
          {tabValue === 0 && (
            <Box>
              <Box 
                sx={{ 
                  mb: 3, 
                  p: 2, 
                  bgcolor: '#F0F9FF', 
                  border: '1px solid #E0F2FE',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Box 
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    bgcolor: '#0EA5E9', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'white'
                  }}
                >
                  ℹ️
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} color="#0F172A">
                    How Transactions Work
                  </Typography>
                  <Typography variant="body2" color="#64748B">
                    Transactions are automatically created from payments (income) and expenses. 
                    To add new transactions, create appointments with payments or add expenses in the Expenses tab.
                  </Typography>
                </Box>
              </Box>
              <TransactionList />
            </Box>
          )}

          {tabValue === 1 && (
            <ExpenseList />
          )}

          {tabValue === 2 && (
            <ReportGenerator />
          )}
        </Box>
      </Card>
      </Fade>
    </Box>
  );
};

export default Finance;